import React, { useCallback, useMemo } from 'react';

// Styles & assets

import styles from '../styles/Columns.module.css';
import chatIcon from '../../src/assets/icons/chat-icon.svg';

// Services

// Services
import { getPatientTimeAdmit } from '../services/Patient';
import { getAgePatient, getZonePatient, getFullName, getAddress, getTownNamePatient, getAllergiesPatient, getDiagnosisPatient, getPreconfigPatient, getPatientBedName } from '../services/getPatientAtribute';
import ProfileContext from './ProfileContext';

import { useState, useContext } from 'react';
import useUIPersistance from '../hooks/useUIPersistance';

import ViewFileLink from '../components/PatientsPage/ViewFileLink/ViewFileLink';
import SonarTableChart from '../components/PatientsPage/SonarTableChart/SonarTableChart';
import PatientAlerts from '../components/Helpers/PatientAlerts/PatientAlerts';

import { Column } from '../interfaces/Columns';
import { Patient } from '../interfaces/Patient';

import { useTranslation } from 'react-i18next';
import ButtonStayTime from '../components/PatientsPage/ButtonStayTime/ButtonStayTime';

import moment from 'moment';

interface DispatchColumnVisible {
    type: 'TOGGLE_patient_COLUMN_VISIBILITY' | 'TOGGLE_sonar_COLUMN_VISIBILITY'
    id: number
}
type DispatchDefaultVisibility = { type: 'SET_DEFAULT_VISIBILITY', patientIDs: number[], sonarIDs: number[] }
type DispatchAddScoreColumns = { type: 'ADD_SCORE_COLUMNS', columns: ColumnWithoutVisible<Patient>[] }
type DispatchColumns = DispatchColumnVisible | DispatchDefaultVisibility | DispatchAddScoreColumns;

type DispatchSelectedCells =
    { type: 'SET_CELL', rowID: number, colID: number } |
    { type: 'SET_DEFAULT', cellTuples: { [key: string]: number } };

interface IColumnsContext {
    columnsPatients: Column<Patient>[]
    columnsSonar: Column<Patient>[],
    dispatchColumns: (dispatch: DispatchColumns) => void
    cellsSonarSelected: { [key: number]: number }
    dispatchSonarSelectedCells: (dispatch: DispatchSelectedCells) => void
}

type ColumnWithoutVisible<T> = Omit<Column<T>, 'visible'>;

const ColumnsContext = React.createContext<IColumnsContext>({
    columnsPatients: [],
    columnsSonar: [],
    dispatchColumns: (_dispatch: DispatchColumns) => undefined,
    cellsSonarSelected: {},
    dispatchSonarSelectedCells: (_dispatch: DispatchSelectedCells) => undefined
});

export const ColumnsContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const { t } = useTranslation();

    const { profileAttributes } = useContext(ProfileContext);
    const { set } = useUIPersistance();

    const getExtraColumnVisibility = useCallback((id: number) => {
        if (profileAttributes === null) return null;
        if (id === 16) return profileAttributes.tableHasSilencePact;
        if (id === 17) return profileAttributes.tableHasSouthAttention;
        if (id === 18) return profileAttributes.tableHasSupportResource;
        if (id === 19) return profileAttributes.tableHasTerminalPhase;
        return null;
    }, [profileAttributes]);

    const sortTimeAdmit = (patient: Patient) => {
        const endDate = patient.cases?.[0]?.end_date;
        const startDate = patient.cases?.[0]?.start_date;
        if (endDate !== null || endDate === undefined || startDate === undefined) return '0';
        return moment().diff(moment(startDate), 'm').toString();
    };

    const DEFAULT_COLUMNS_PATIENTS: Column<Patient>[] = [
        {
            id: 1,
            name: () => '',
            editVisiblilityDisabled: true,
            visible: true,
            sortable: false,
            element: (patient) =>
                <PatientAlerts
                    inquiryAlerts={patient.cases[0]?.patient_inquiry_alerts}
                    patientScoreAlerts={patient.cases[0]?.patient_score_param_alerts}
                    patientID={patient.id}
                />,
        },
        {
            id: 2,
            name: () => t('name'),
            visible: true,
            sortable: true,
            cell: (patient) => getFullName(patient),
        },
        {
            id: 3,
            name: () => t('bed'),
            visible: true,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => getPatientBedName(patient),
        },
        {
            id: 4,
            name: () => t('zone'),
            visible: true,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => getZonePatient(patient),
        },
        {
            id: 5,
            name: () => t('NHC'),
            visible: true,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => `${patient.mrn || ''}`,
        },
        {
            id: 6,
            name: () => t('CIP'),
            visible: false,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => `${patient.pid_1 || ''}`,
        },
        {
            id: 25,
            name: () => 'CAP',
            visible: true,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => `${patient.primary_care_center?.name || ''}`,
        },
        {
            id: 27,
            name: () => t('environment'),
            visible: true,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => patient.cases[0]?.follow_ups?.[0]?.profile.name ?? '',
        },
        {
            id: 7,
            name: () => t('study'),
            visible: false,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => patient.cases[0]?.follow_ups?.[0]?.profile.name ?? ''
        },
        {
            id: 8,
            name: () => t('age'),
            visible: false,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => `${getAgePatient(patient)}`,
        },
        {
            id: 22,
            name: () => t('preconfiguration'),
            visible: true,
            editVisiblilityDisabled: false,
            sortable: true,
            cell: (patient) => getPreconfigPatient(patient),
        },
        {
            id: 9,
            name: () => t('gender'),
            visible: false,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => `${patient.sex?.name || 'D'}`,
        },
        {
            id: 10,
            name: () => t('diagnosis'),
            visible: false,
            sortable: true,
            cell: (patient) => `${getDiagnosisPatient(patient)}`,
        },
        {
            id: 24,
            name: () => t('time_admit'),
            visible: true,
            sortable: true,
            element: (patient) => <p>{getPatientTimeAdmit(patient)}</p>,
            sortElementFunc: (patient) => sortTimeAdmit(patient)
        },
        {
            id: 26,
            name: () => 'Chat',
            visible: true,
            sortable: false,
            element: (patient) => {
                if (!patient?.chatInfo || !patient.chatInfo.has_activity) {
                    return <></>;
                }

                return (
                    <img
                        src={chatIcon}
                        className={patient.chatInfo.user_has_unread_msg ? styles.iconChatBlue : ''}
                        alt="chat"
                    />
                );
            }
        },
        {
            id: 11,
            name: () => t('allergies'),
            visible: false,
            sortable: true,
            cell: (patient) => getAllergiesPatient(patient),
        },
        {
            id: 12,
            name: () => t('town'),
            visible: false,
            sortable: true,
            cell: (patient) => getTownNamePatient(patient),
        },
        {
            id: 13,
            name: () => t('phone'),
            visible: false,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => `${patient.mobile_phone || ''}`,
        },
        {
            id: 14,
            name: () => t('address'),
            visible: true,
            sortable: true,
            cell: (patient) => getAddress(patient),
        },
        ...(window.config.STAY_TIME_ENABLED ? [{
            id: 24,
            name: () => t('stay_time'),
            visible: true,
            sortable: false,
            element: (patient: Patient) => <ButtonStayTime patient={patient} />
        }] : []),
        {
            id: 15,
            name: () => t('main_carer'),
            visible: true,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => patient.carers && patient.carers.length > 0 ? patient.carers[0].first_name + ' ' + patient.carers[0].last_name_1 : '',
        },
        {
            id: 23,
            name: () => t('main_carer_telephone'),
            visible: true,
            sortable: true,
            classHeader: styles.centerColHeader,
            classBody: styles.centerColBody,
            cell: (patient) => patient.carers && patient.carers.length > 0 ? patient.carers[0].mobile_phone || patient.carers[0].home_phone || '' : '',
        },
        // {
        //     id: 16,
        //     name: () => t('conspiracy_silence'),
        //     visible: true,
        //     sortable: true,
        //     classHeader: styles.centerColHeader,
        //     classBody: styles.centerColBody,
        //     cell: (patient) => `${patient.conspiracy_silence === undefined ? '' : patient.conspiracy_silence ? t('yes') : t('no')}`,
        // },
        // {
        //     id: 17,
        //     name: () => t('sud_atention'),
        //     visible: true,
        //     sortable: true,
        //     classHeader: styles.centerColHeader,
        //     classBody: styles.centerColBody,
        //     cell: (patient) => `${patient.sud_atention || ''}`,
        // },
        // {
        //     id: 18,
        //     name: () => t('support_resource'),
        //     visible: true,
        //     sortable: false,
        //     cell: (patient) => `${patient.support_resource || ''}`,
        // },
        // {
        //     id: 19,
        //     name: () => t('stage'),
        //     visible: true,
        //     sortable: false,
        //     classBody: 'phaseTable',
        //     element: (patient) => <>{patient.pps !== null && patient.pps !== undefined ?
        //         <> <span>{t('stage')}</span> <CurrentStage pps={patient.pps} /></>
        //         : ''}</>,
        // },
        {
            id: 20,
            name: () => t('comment'),
            visible: true,
            editVisiblilityDisabled: true,
            sortable: false,
            cell: (patient) => `${patient.comment || ''}`,
        },
        {
            id: 21,
            name: () => '',
            visible: true,
            editVisiblilityDisabled: true,
            sortable: false,
            element: (patient) => (<ViewFileLink link={`/home/patients/cases/${patient.cases[0]?.id}`} />)
        }
    ];

    const DEFAULT_COLUMNS_SONAR: Column<Patient>[] = useMemo(() => {
        return [
            {
                id: 118,
                name: () => 'VIG',
                visible: true,
                sortable: true,
                selectableCell: true,
                cell: (patient) => patient.VIG?.toString() ?? '-',
            },
            {
                id: 103,
                name: () => t('name'),
                visible: true,
                sortable: true,
                cell: (patient) => getFullName(patient),
            },
            {
                id: 104,
                name: () => t('bed'),
                visible: false,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                cell: (patient) => getPatientBedName(patient),
            },
            {
                id: 105,
                name: () => t('NHC'),
                visible: false,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                cell: (patient) => `${patient.mrn || ''}`,
            },
            {
                id: 106,
                name: () => t('preconfig'),
                visible: false,
                editVisiblilityDisabled: true,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                cell: (patient) => patient.cases[0]?.follow_ups?.[0]?.profile.name ?? ''
            },
            {
                id: 107,
                name: () => t('age'),
                visible: true,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                cell: (patient) => `${getAgePatient(patient)}`,
            },
            {
                id: 108,
                name: () => t('gender'),
                visible: false,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                cell: (patient) => `${patient.sex?.name || ''}`,
            },
            {
                id: 117,
                name: () => t('time_admit'),
                visible: true,
                sortable: true,
                element: (patient) => <p>{getPatientTimeAdmit(patient)}</p>,
                sortElementFunc: (patient) => sortTimeAdmit(patient)
            },
            {
                id: 109,
                name: () => t('HR'),
                visible: true,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                selectableCell: true,
                cell: (patient) => `${patient.FC_EKG || '-'}`,
            },
            {
                id: 110,
                name: () => t('SPO2'),
                visible: true,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                selectableCell: true,
                cell: (patient) => `${patient.PULSIOX || '-'}`,
            },
            {
                id: 111,
                name: () => t('SBP'),
                visible: true,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                selectableCell: true,
                cell: (patient) => `${patient.PA_S || '-'}`,
            },
            {
                id: 112,
                name: () => t('DBP'),
                visible: false,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                selectableCell: true,
                cell: (patient) => `${patient.PA_D || '-'}`,
            },
            {
                id: 113,
                name: () => t('temperature'),
                visible: true,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                selectableCell: true,
                cell: (patient) => `${patient.TEMP || '-'}`,
            },
            {
                id: 114,
                name: () => t('weight'),
                visible: true,
                sortable: true,
                classHeader: styles.centerColHeader,
                classBody: styles.centerColBody,
                selectableCell: true,
                cell: (patient) => `${patient.WEIGHT || '-'}`,
            },
            {
                id: 115,
                name: () => t('trend'),
                visible: true,
                editVisiblilityDisabled: true,
                sortable: false,
                element: (patient) => (
                    <div style={{ width: '15rem', height: '4.5rem' }}>
                        <SonarTableChart case_id={patient.cases[0].id} patientID={patient.id} />
                    </div>
                )
            },
            {
                id: 116,
                name: () => '',
                visible: true,
                editVisiblilityDisabled: true,
                sortable: false,
                element: (patient) => (<ViewFileLink link={`/home/patients/cases/${patient.cases[0]?.id}`} />)
            }
        ];
    }, [t]);

    const [columnsPatients, setColumnsPatients] = useState<Column<Patient>[]>(DEFAULT_COLUMNS_PATIENTS);
    const [columnsScores, setColumnsScores] = useState<ColumnWithoutVisible<Patient>[]>([]);
    const DEFAULT_SONAR_COLUMNS_VISIBLE = [103, 107, 109, 110, 111, 112, 113, 114, 115, 116];
    const [columnsSonarVisible, setColumnsSonarVisible] = useState<number[]>(DEFAULT_SONAR_COLUMNS_VISIBLE);
    const [cellsSonarSelected, setCellsSonarSelected] = useState<{ [key: string]: number }>({});

    const handleSetColumnsVisibility = useCallback((ids: number[], oldColumns: Column<Patient>[]) =>
        oldColumns.map(column => ({ ...column, visible: ids.includes(column.id) })), []);

    const handleToggleColumnVisibility = useCallback((id: number, oldColumns: Column<Patient>[]) => {
        const newColumns = oldColumns.map(column => {
            if (column.id !== id) return column;
            const newColumn = { ...column };
            return { ...newColumn, visible: !newColumn.visible };
        });
        set('bchome_columns_patientIDs', newColumns.filter(col => col.visible).map(col => col.id).join());
        return newColumns;
    }, [set]);

    const dispatchColumns = useCallback((dispatch: DispatchColumns) => {
        //console.log('dispatchColumns type', dispatch.type, dispatch);

        if (dispatch.type === 'TOGGLE_patient_COLUMN_VISIBILITY')
            setColumnsPatients(oldColumns => handleToggleColumnVisibility(dispatch.id, oldColumns));
        if (dispatch.type === 'TOGGLE_sonar_COLUMN_VISIBILITY') {
            setColumnsSonarVisible(oldColumnsIds => {
                const newColumnsIds = oldColumnsIds.includes(dispatch.id) ?
                    oldColumnsIds.filter(id => id !== dispatch.id)
                    : [...oldColumnsIds, dispatch.id];
                set('bchome_columns_sonarIDs', newColumnsIds.join());
                return newColumnsIds;
            });
        }
        if (dispatch.type === 'SET_DEFAULT_VISIBILITY') {
            if (dispatch.patientIDs.length > 0) setColumnsPatients(oldColumns => handleSetColumnsVisibility(dispatch.patientIDs, oldColumns));
            if (dispatch.sonarIDs.length > 0) setColumnsSonarVisible(dispatch.sonarIDs);
        }
        if (dispatch.type === 'ADD_SCORE_COLUMNS') {
            setColumnsScores(dispatch.columns);
        }
    }, [handleSetColumnsVisibility, handleToggleColumnVisibility, set]);

    const dispatchSonarSelectedCells = useCallback((dispatch: DispatchSelectedCells) => {
        if (dispatch.type === 'SET_CELL') setCellsSonarSelected(colsSelected => {
            const newCols = { ...colsSelected };
            if (newCols[dispatch.rowID] === dispatch.colID) delete newCols[dispatch.rowID];
            else newCols[dispatch.rowID] = dispatch.colID;
            set('bchome_selected_sonar_IDs', JSON.stringify(newCols));
            return newCols;
        });
        if (dispatch.type === 'SET_DEFAULT') setCellsSonarSelected(dispatch.cellTuples);
    }, [set]);

    const newSonarColumns: Column<Patient>[] = useMemo(() => [...columnsScores, ...DEFAULT_COLUMNS_SONAR].map(c => ({
        ...c,
        visible: columnsSonarVisible.includes(c.id)
    })), [DEFAULT_COLUMNS_SONAR, columnsScores, columnsSonarVisible]);

    /*console.log('columnsPatientsNew', columnsPatientsNew.map(col => ({
        id: col.id,
        name: col.name(),
        visible: col.visible
    })));*/

    const initState = {
        columnsPatients: columnsPatients,
        dispatchColumns,
        columnsSonar: newSonarColumns,
        cellsSonarSelected,
        dispatchSonarSelectedCells
    };

    return (
        <ColumnsContext.Provider value={initState}>
            {children}
        </ColumnsContext.Provider>
    );
};

export default ColumnsContext;